import React, { useState, useEffect } from 'react';
import { 
  Globe, Search, Edit2, Save, X, ChevronDown, ChevronUp, 
  CheckCircle, AlertCircle, Filter, RefreshCw, Plus
} from 'lucide-react';

const PageSEOManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/seo/pages`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializePages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/seo/pages/init`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Error initializing pages:', error);
    }
  };

  const startEditing = (page) => {
    setEditingPage(page.page_name);
    setEditForm({
      title_tag: page.title_tag || '',
      meta_description: page.meta_description || '',
      focus_keyword: page.focus_keyword || '',
      keywords: (page.keywords || []).join(', '),
      is_indexable: page.is_indexable !== false,
      og_title: page.og_title || '',
      og_description: page.og_description || ''
    });
  };

  const cancelEditing = () => {
    setEditingPage(null);
    setEditForm({});
  };

  const savePage = async (pageName) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/seo/pages/${pageName}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title_tag: editForm.title_tag,
          meta_description: editForm.meta_description,
          focus_keyword: editForm.focus_keyword || null,
          keywords: editForm.keywords ? editForm.keywords.split(',').map(k => k.trim()).filter(k => k) : [],
          is_indexable: editForm.is_indexable,
          og_title: editForm.og_title || null,
          og_description: editForm.og_description || null
        })
      });

      if (response.ok) {
        await fetchPages();
        setEditingPage(null);
        setEditForm({});
      }
    } catch (error) {
      console.error('Error saving page SEO:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleRowExpanded = (pageName) => {
    setExpandedRows(prev => ({
      ...prev,
      [pageName]: !prev[pageName]
    }));
  };

  const filteredPages = pages.filter(page =>
    page.page_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (page.title_tag || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="text-blue-600" />
            Main Pages ({filteredPages.length})
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Select a row to edit each page's basic SEO settings.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={initializePages}
            className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            Reset to Defaults
          </button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <Filter size={16} />
          Filter
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-blue-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Page name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Page URL
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  <span className="flex items-center gap-1">
                    Focus key...
                    <AlertCircle size={14} className="text-gray-400" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  <span className="flex items-center gap-1">
                    Title tag
                    <AlertCircle size={14} className="text-gray-400" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  <span className="flex items-center gap-1">
                    Meta description
                    <AlertCircle size={14} className="text-gray-400" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  <span className="flex items-center gap-1">
                    Indexable
                    <AlertCircle size={14} className="text-gray-400" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPages.map((page) => (
                <React.Fragment key={page.page_name}>
                  <tr 
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                      editingPage === page.page_name ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => !editingPage && toggleRowExpanded(page.page_name)}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" className="rounded border-gray-300" onClick={(e) => e.stopPropagation()} />
                        <span className="font-medium text-gray-900">{page.page_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <a 
                        href={page.page_url} 
                        className="text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {page.page_url}
                      </a>
                    </td>
                    <td className="px-4 py-4">
                      {page.focus_keyword ? (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          + Add Keyword
                        </span>
                      ) : (
                        <button className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200">
                          + Add Keyword
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="max-w-[200px]">
                        {page.title_tag !== page.page_name ? (
                          <span className="text-xs text-green-600 font-medium">EDITED</span>
                        ) : null}
                        <p className="text-sm text-gray-900 truncate">{page.title_tag}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="max-w-[200px]">
                        {page.meta_description ? (
                          <span className="text-xs text-green-600 font-medium">EDITED</span>
                        ) : null}
                        <p className="text-sm text-gray-600 truncate">{page.meta_description || 'No description'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {page.is_indexable !== false ? (
                        <CheckCircle size={20} className="text-green-500" />
                      ) : (
                        <X size={20} className="text-red-500" />
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(page);
                        }}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Edit Row */}
                  {editingPage === page.page_name && (
                    <tr className="bg-blue-50">
                      <td colSpan={7} className="px-4 py-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Title Tag
                            </label>
                            <input
                              type="text"
                              value={editForm.title_tag}
                              onChange={(e) => setEditForm(prev => ({ ...prev, title_tag: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500 mt-1">{editForm.title_tag.length}/60</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Focus Keyword
                            </label>
                            <input
                              type="text"
                              value={editForm.focus_keyword}
                              onChange={(e) => setEditForm(prev => ({ ...prev, focus_keyword: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Main keyword for this page"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Meta Description
                            </label>
                            <textarea
                              value={editForm.meta_description}
                              onChange={(e) => setEditForm(prev => ({ ...prev, meta_description: e.target.value }))}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500 mt-1">{editForm.meta_description.length}/160</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              OG Title (Social)
                            </label>
                            <input
                              type="text"
                              value={editForm.og_title}
                              onChange={(e) => setEditForm(prev => ({ ...prev, og_title: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Title for social sharing"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Keywords (comma-separated)
                            </label>
                            <input
                              type="text"
                              value={editForm.keywords}
                              onChange={(e) => setEditForm(prev => ({ ...prev, keywords: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="keyword1, keyword2, keyword3"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              OG Description (Social)
                            </label>
                            <textarea
                              value={editForm.og_description}
                              onChange={(e) => setEditForm(prev => ({ ...prev, og_description: e.target.value }))}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Description for social sharing"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editForm.is_indexable}
                                onChange={(e) => setEditForm(prev => ({ ...prev, is_indexable: e.target.checked }))}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Allow indexing</span>
                            </label>
                          </div>
                          <div className="flex justify-end gap-3 md:col-span-2">
                            <button
                              onClick={cancelEditing}
                              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => savePage(page.page_name)}
                              disabled={saving}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                              {saving ? (
                                <RefreshCw size={16} className="animate-spin" />
                              ) : (
                                <Save size={16} />
                              )}
                              Save Changes
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PageSEOManager;
